// Cliente 360 — pestaña Visitas: historico de visitas de cobro del cliente
// (GET /admin/clientes/:id/visitas, Fase D). Las visitas NO son pagos: nunca
// tocan saldos, KPIs ni recibos; solo el resultado y el comentario del gestor.
import { useState } from 'react';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { useHasPermission } from '../../../auth/useHasPermission';
import { VisitaCreateModal } from '../../visitas/components/VisitaCreateModal';
import { VisitaResultadoTag } from '../../visitas/components/VisitaResultadoTag';
import { useClienteVisitas } from '../api/useClientes';
import { paginationProps } from '../../../lib/paginacion';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';
import { useT } from '../../../locales';
import { Permission, type VisitaCobroResponse } from '@aid/shared';
import { parseDateOnly } from '../../../lib/date';

const DATE_FORMAT = 'DD/MM/YYYY';

interface ClienteVisitasListProps {
  clienteId: number;
}

export function ClienteVisitasList({ clienteId }: ClienteVisitasListProps) {
  const t = useT();
  const { page, pageSize, sort, handleChange } = useServerPaginacion<VisitaCobroResponse>();
  const canCreate = useHasPermission(Permission.VISITAS_CREATE);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useClienteVisitas(clienteId, {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...sort,
  });

  const createAction = canCreate ? (
    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
      {t('pages.visitas.create')}
    </Button>
  ) : undefined;

  const columns = [
    {
      title: t('pages.visitas.fecha'),
      key: 'fecha',
      ...serverSortProps('fecha', sort),
      render: (_: unknown, record: VisitaCobroResponse) =>
        // Date-only contract: parse component-wise so the visit day is exact.
        parseDateOnly(record.fecha)?.format(DATE_FORMAT) ?? '—',
    },
    {
      title: t('pages.visitas.resultado'),
      key: 'resultado',
      ...serverSortProps('resultado', sort),
      render: (_: unknown, record: VisitaCobroResponse) => (
        <VisitaResultadoTag resultado={record.resultado} />
      ),
    },
    {
      title: t('pages.visitas.comentario'),
      key: 'comentario',
      render: (_: unknown, record: VisitaCobroResponse) => record.comentario ?? '-',
      ellipsis: true,
    },
    {
      title: t('pages.visitas.usuario'),
      key: 'usuario',
      ...serverSortProps('usuario', sort),
      render: (_: unknown, record: VisitaCobroResponse) => record.usuario?.username ?? '-',
    },
  ];

  if (error) {
    return (
      <div>
        <EmptyState compact title={t('pages.visitas.errorLoad')} />
      </div>
    );
  }

  if ((!data?.data || data.data.length === 0) && !isLoading) {
    return (
      <div>
        <EmptyState
          compact
          title={t('pages.clientes.actividad.sinVisitas')}
          description={t('pages.clientes.actividad.sinVisitasDesc')}
          action={createAction}
        />
        <VisitaCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={refetch}
          preselectClienteId={clienteId}
        />
      </div>
    );
  }

  return (
    <div>
      {canCreate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          {createAction}
        </div>
      )}
      <DataTable<VisitaCobroResponse>
        columns={columns}
        dataSource={data?.data}
        rowKey="id"
        loading={isLoading}
        pagination={paginationProps(t, { page, pageSize, total: data?.total ?? 0 })}
        onChange={handleChange}
      />
      <VisitaCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refetch}
        preselectClienteId={clienteId}
      />
    </div>
  );
}
